/* eslint-disable @typescript-eslint/await-thenable */

import { createModel, RematchDispatch, RematchRootState } from '@rematch/core';
import { RootModel } from '.';
import { Project, ProjectsState, ProjectType } from '../types';
import { getProjects } from '../repositories/projectRepository';
import { ProjectsApi } from '../apis';
import { UserSync } from '../services';
import * as Constants from '../utils/constants';

const initialProjectsState: ProjectsState = {
  all: [],
  favorites: [],
  mrus: [],
  searched: [],
  searchPhrase: undefined,
  projectTab: 'my-projects',
  projectsLoaded: false,
  runSearch: false,
  project: {
    name: '',
    id: '0',
    isExternal: false,
    number: '',
    solutionType: '',
  },
};

export const projects = createModel<RootModel>()({
  state: initialProjectsState,
  reducers: {
    setProjects(state: ProjectsState, projectsPayload: any): ProjectsState {
      return {
        ...state,
        [projectsPayload.projectType]: projectsPayload.projects,
      };
    },
    setProject(state: ProjectsState, project: Project): ProjectsState {
      return {
        ...state,
        project,
      };
    },
    setProjectsTab(state: ProjectsState, tabId: string): ProjectsState {
      return {
        ...state,
        projectTab: tabId,
      };
    },
    setProjectsLoaded(
      state: ProjectsState,
      projectsLoaded: boolean
    ): ProjectsState {
      return {
        ...state,
        projectsLoaded,
      };
    },
    setRunSearch(state: ProjectsState, runSearch: boolean): ProjectsState {
      return {
        ...state,
        runSearch,
      };
    },
    setSearchPhrase(state: ProjectsState, searchPhrase: string): ProjectsState {
      return {
        ...state,
        searchPhrase,
      };
    },
  },
  effects: (dispatch: RematchDispatch<RootModel>) => ({
    async loadProjects(
      _,
      rootState: RematchRootState<RootModel>
    ): Promise<void> {
      try {
        if (rootState.projects.runSearch) {
          if (!rootState.projects.searchPhrase) {
            this.setProjects({
              projectType: 'searched',
              projects: [],
            });
          } else {
            this.setProjects({
              projectType: 'searched',
              projects: await getProjects(
                'searched',
                rootState.projects.searchPhrase
              ),
            });
          }
        }

        if (!rootState.projects.projectsLoaded) {
          this.setProjects({
            projectType: 'mrus',
            projects: await getProjects('mrus'),
          });

          this.setProjects({
            projectType: 'favorites',
            projects: await getProjects('favorites'),
          });

          this.setProjects({
            projectType: 'all',
            projects: await getProjects('all'),
          });
        }
      } catch (error) {
        dispatch.global.handleError({ error });
      }

      this.setProjectsLoaded(true);
    },

    async loadProjectFromTabData(_, rootState: RematchRootState<RootModel>) {
      const tabData = rootState.tab.tabData;
      if (!tabData) return;

      try {
        this.setProject(
          await ProjectsApi.fetchProjectDetails(tabData.projectId)
        );
      } catch (error) {
        const userSyncAvailable =
          tabData.serviceName === 'ProjectWise Web Connections' ||
          tabData.serviceId ===
            Constants.ServiceRegistry.projectWiseWebConnectionsId;

        let userSyncSuccessful = true;
        if (userSyncAvailable) {
          userSyncSuccessful = await UserSync.syncUserToProject(
            tabData.projectId
          );
          if (userSyncSuccessful) {
            try {
              this.setProject(
                await ProjectsApi.fetchProjectDetails(tabData.projectId)
              );
            } catch {
              userSyncSuccessful = false;
            }
          }
        }
        if (!userSyncSuccessful || !userSyncAvailable) {
          dispatch.global.handleError({ error });
        }
      }
    },

    async filterProjects(search: {
      phrase: string;
      projectType: ProjectType;
    }): Promise<void> {
      const instances = (await getProjects(search.projectType)).filter(
        (project: Project) =>
          project.name?.toLowerCase().includes(search.phrase.toLowerCase()) ||
          project.number?.toLowerCase().includes(search.phrase.toLowerCase())
      );
      this.setProjects({
        projectType: search.projectType,
        projects: instances,
      });
    },
  }),
});
